import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Grid3x3, PenTool, ShoppingBag, User } from 'lucide-react';
import { useCartStore } from '@/store/cartStore';
import { useAuthStore } from '@/store/authStore';
import { buildLoginHref } from '@/lib/auth';

/**
 * Native-mobile-app bottom tab bar. Fixed to the viewport bottom on mobile only
 * (hidden >= 768px via CSS). Five tabs: Home / Categories / Design / Cart / Account.
 * Active tab is inferred from the current pathname.
 *
 * The parent layout adds `padding-bottom: var(--mobile-nav-height)` to <main> on
 * mobile so content does not sit under this bar. iOS safe-area-inset-bottom is
 * added via CSS so the bar clears the home indicator on iPhones with notches.
 */
export default function MobileBottomNav() {
  const location = useLocation();
  const pathname = location.pathname;
  const { itemCount } = useCartStore();
  const { isAuthenticated } = useAuthStore();
  const loginHref = buildLoginHref(pathname, location.search);

  const tabs = [
    { href: '/', label: 'Home', icon: Home, match: (p: string) => p === '/' },
    { href: '/categories', label: 'Categories', icon: Grid3x3, match: (p: string) => p.startsWith('/categories') || p.startsWith('/products') },
    { href: '/design', label: 'Design', icon: PenTool, match: (p: string) => p.startsWith('/design') },
    { href: '/cart', label: 'Cart', icon: ShoppingBag, match: (p: string) => p.startsWith('/cart') || p.startsWith('/checkout'), badge: itemCount },
    { href: isAuthenticated ? '/account' : loginHref, label: isAuthenticated ? 'Account' : 'Sign In', icon: User, match: (p: string) => p.startsWith('/account') || p.startsWith('/orders') || p.startsWith('/login') },
  ];

  return (
    <nav className="mobile-bottom-nav" aria-label="Primary">
      {tabs.map((tab) => {
        const active = tab.match(pathname);
        const Icon = tab.icon;
        return (
          <Link
            key={tab.href}
            to={tab.href}
            className={`mobile-bottom-nav-item ${active ? 'is-active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            <span className="mobile-bottom-nav-icon">
              <Icon size={22} strokeWidth={active ? 2.25 : 1.75} />
              {typeof tab.badge === 'number' && tab.badge > 0 && (
                <span className="mobile-bottom-nav-badge">
                  {tab.badge > 99 ? '99+' : tab.badge}
                </span>
              )}
            </span>
            <span className="mobile-bottom-nav-label">{tab.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
